import { ArrayShape, CallExpressionShape, ObjectShape, PropertyAccessShape } from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import { slugifyString } from '../../utils/slugify.js'
import {
    ACTIVITY_DEFAULTS,
    ACTIVITY_GENERATED_CALLEE,
    decodeKeywordsToken,
    SYS_ELEMENT_MAPPING_TABLE,
    SYS_VARIABLE_VALUE_TABLE,
} from '../constants/playbook-plugin-constants'
import {
    ACTIVITY_DEF_SYS_ID_TO_KEY,
    ACTIVITY_DEF_INPUT_DISPLAY_PREFS,
    ACTIVITY_DEF_EXPERIENCE_DISPLAY_PREFS,
    DECISION_BRANCHES_VAR_SYS_ID,
    DECISION_DEF_SYS_ID,
    INPUT_SYS_ID_TO_FLUENT,
    EXPERIENCE_PROPERTY_SYS_ID_TO_FLUENT,
    type ResolvedVariable,
} from '../constants/activity-ootb-maps'
import { resolveStartRuleToShape } from '../utils/start-rule-resolver'
import { convertToTemplateValue, convertValueToType } from '../utils/transform-utils'
import type { Database, Record as SdkRecord, Shape } from '@servicenow/sdk-build-core'
import { findTimerRecord, timerAttributesToShape } from './timer-attributes-transform'
import {
    type PillTransformContext,
    transformConditionPills,
    transformStandardPills,
} from '../utils/playbook-pill-transformer'

/**
 * Resolves a variable entry into a Shape for use as an input or experience property.
 * Prefers the sys_element_mapping value (pill reference) over sys_variable_value when present.
 * Returns undefined if the field is excluded by allowedFields.
 */
function resolveVarEntryToShape(
    entry: ResolvedVariable,
    allowedFields: Set<string> | undefined,
    elementMappingByField: Map<string, string>,
    varRecord: SdkRecord,
    pillContext: PillTransformContext | undefined
): Shape | undefined {
    if (allowedFields !== undefined && !allowedFields.has(entry.name)) {
        return undefined
    }
    const value = varRecord.get('value')
    const str = value.ifString()?.getValue()
    const pillStr = elementMappingByField.get(entry.name)
    const effectiveStr = pillStr || str
    if (entry.type === 'template_value') {
        return convertToTemplateValue(value, effectiveStr, pillContext)
    }
    const transformed =
        effectiveStr && pillContext ? transformStandardPills(effectiveStr, varRecord, pillContext) : undefined
    return transformed ?? convertValueToType(value, entry.type)
}

export interface ActivityToShapeResult {
    success: true
    value: CallExpressionShape
    /** For Decision activities: maps branch GUID → Fluent branch id (slugified label or 'else') */
    branchMap?: Map<string, string>
}

export function activityRecordToShape(
    record: SdkRecord,
    database: Database,
    guidToVarName?: Map<string, string>,
    branchGuidMap?: Map<string, { activityVarName: string; fluentBranchId: string }>,
    pillContext?: PillTransformContext,
    timerRecords?: SdkRecord[]
): ActivityToShapeResult | { success: false } {
    const { startRuleShape } = resolveStartRuleToShape(record, database, guidToVarName, branchGuidMap)

    // Build an activity-scoped pill context with the current activity's sys_id
    const activityPillContext: PillTransformContext | undefined = pillContext
        ? { ...pillContext, currentActivitySysId: record.getId().getValue() }
        : undefined

    // Find matching timer record for this activity
    const timerRecord = timerRecords
        ? findTimerRecord(timerRecords, record.getId().getValue(), 'sys_pd_activity')
        : undefined

    // The AI-agent fields are only meaningful when an agent is enabled, so the whole block
    // below is gated on enable_ai_agent. Read the raw column up front to drive that gate.
    const isAiAgentEnabled = record.get('enable_ai_agent')?.toBoolean().getValue()

    const activityDefSysId = record.get('activity_definition')?.ifString()?.getValue() ?? ''
    // Decision activities branch on per-branch conditions, not a top-level
    // conditionToRun, so we omit conditionToRun from their config.
    const isDecisionActivity = activityDefSysId === DECISION_DEF_SYS_ID

    const configShape = record.transform(({ $ }) => ({
        $id: $.val(NowIdShape.from(record)),
        label: $.from('label'),
        name: $.from('name'),
        order: $.from('order').toNumber(),
        description: $.from('description'),
        startRule: $.val(startRuleShape),
        ...(isDecisionActivity
            ? {}
            : {
                  conditionToRun: $.from('condition_to_run').map((v: Shape) => {
                      if (!activityPillContext) {
                          return v
                      }
                      const str = v.ifString()?.getValue()
                      if (!str) {
                          return v
                      }
                      return transformConditionPills(str, record, activityPillContext) ?? v
                  }),
              }),
        restartRule: $.from('restart_rule'),
        // AI-agent fields are internal round-trip-only and only meaningful when the agent
        // is enabled. When enable_ai_agent is true we surface enableAiAgent plus each
        // populated field; when it is false we omit the entire AI block. Within the block,
        // empty strings/lists are omitted while populated values and the booleans are
        // preserved as-is. aiAgentRunAs is the one field with a stable platform default
        // ('playbook_user'), so we omit it when it matches that default.
        ...(isAiAgentEnabled
            ? {
                  enableAiAgent: $.from('enable_ai_agent').toBoolean(),
                  aiAgentObjective: $.from('ai_agent_objective').def(''),
                  aiAgentObjAdditionalDetail: $.from('ai_agent_obj_additional_detail').def(''),
                  aiAgentExecutionMode: $.from('ai_agent_execution_mode').def(''),
                  aiAgentRunWithRoles: $.from('ai_agent_run_with_roles')
                      .map((v: Shape) => {
                          const str = v.ifString()?.getValue() ?? ''
                          return str
                              ? str
                                    .split(',')
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                              : []
                      })
                      .def([]),
                  aiAgentRunAs: $.from('ai_agent_run_as').def(ACTIVITY_DEFAULTS.AI_AGENT_RUN_AS),
                  aiAgentUser: $.from('ai_agent_user').def(''),
                  conversationalAgents: $.from('conversational_agents')
                      .map((v: Shape) => {
                          const str = v.ifString()?.getValue() ?? ''
                          return str
                              ? str
                                    .split(',')
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                              : []
                      })
                      .def([]),
                  launchAgentWhenActivityStart: $.from('launch_agent_when_activity_start').toBoolean(),
              }
            : {}),
        ...(timerRecord
            ? {
                  startWithDelay: $.val(timerAttributesToShape(timerRecord, activityPillContext)),
              }
            : {}),
    }))

    const activityDefKey = ACTIVITY_DEF_SYS_ID_TO_KEY[activityDefSysId]
    if (!activityDefKey) {
        return { success: false }
    }

    // Determine which input and experience property fields are allowed for this
    // activity definition. Only emit fields that are listed in its inputDisplayPreferences /
    // experienceDisplayPreferences so that internal-only default fields (not
    // intended to be user-configurable) are excluded from the generated Fluent output.
    const allowedInputFields: Set<string> | undefined = ACTIVITY_DEF_INPUT_DISPLAY_PREFS[activityDefSysId]
    const allowedExperienceFields: Set<string> | undefined = ACTIVITY_DEF_EXPERIENCE_DISPLAY_PREFS[activityDefSysId]

    const elements = activityDefKey.split('.')
    if (elements.length < 2) {
        return { success: false }
    }

    const activityDefShape = new PropertyAccessShape({
        source: record,
        elements: elements as [unknown, unknown, ...unknown[]],
    })

    // Build inputs and experienceProperties from sys_variable_value descendants
    const variableValueRecords = database.query(SYS_VARIABLE_VALUE_TABLE, {
        document_key: record.getId().getValue(),
    })

    // Query sys_element_mapping records to recover pill references.
    // The platform stores pill values in sys_element_mapping.value (not
    // sys_variable_value.value), so we must consult both to faithfully
    // round-trip pill-containing fields.
    const elementMappingRecords = database.query(SYS_ELEMENT_MAPPING_TABLE, {
        id: record.getId().getValue(),
    })
    // Inputs and experience properties can share the same field name.
    // Use the `table` column to tell them apart:
    //   inputs:               table = var__m_sys_hub_{flow|action}_input_<sysId>
    //   experience properties: table = var__m_sys_pd_activity_type_prop_<sysId>
    const inputElementMappingByField = new Map<string, string>()
    const expPropElementMappingByField = new Map<string, string>()
    for (const emRecord of elementMappingRecords) {
        const field = emRecord.get('field').ifString()?.getValue()
        const value = emRecord.get('value').ifString()?.getValue()
        const table = emRecord.get('table').ifString()?.getValue() ?? ''
        if (field && value) {
            if (table.includes('sys_pd_activity_type_prop')) {
                expPropElementMappingByField.set(field, value)
            } else {
                inputElementMappingByField.set(field, value)
            }
        }
    }
    const inputs: Record<string, Shape> = {}
    const experienceProperties: Record<string, Shape> = {}
    /** For Decision activities: maps branch GUID → Fluent branch id */
    let branchMap: Map<string, string> | undefined
    for (const varRecord of variableValueRecords) {
        const variableSysId = varRecord.get('variable').ifString()?.getValue()
        if (!variableSysId) {
            continue
        }

        // Decision branches need special JSON parsing (not a plain string value)
        // Match on the exact sys_id to avoid collisions with other automations that may have a 'branches' input
        if (variableSysId === DECISION_BRANCHES_VAR_SYS_ID) {
            const jsonStr = varRecord.get('value').ifString()?.getValue()
            if (jsonStr) {
                try {
                    const parsed = JSON.parse(jsonStr)
                    if (Array.isArray(parsed)) {
                        branchMap = new Map<string, string>()
                        const branchObjects = parsed.map((branch: Record<string, unknown>) => {
                            const branchGuid = String(branch['id'] ?? '')
                            const rawLabel = String(branch['label'] ?? '')
                            const label = branchGuid === 'ELSE' ? 'Else' : rawLabel
                            const fluentId = branchGuid === 'ELSE' ? 'else' : slugifyString(label) || branchGuid
                            const props: Record<string, unknown> = {
                                id: fluentId,
                                label,
                            }
                            if (branch['condition'] !== undefined && branch['condition'] !== '') {
                                const decoded = decodeKeywordsToken(String(branch['condition']))
                                const transformed = activityPillContext
                                    ? transformConditionPills(decoded, varRecord, activityPillContext)
                                    : undefined
                                props['condition'] = transformed ?? decoded
                            }
                            if (branchGuid) {
                                branchMap?.set(branchGuid, fluentId)
                            }
                            return props
                        })
                        inputs['branches'] = new ArrayShape({ source: varRecord, elements: branchObjects })
                    }
                } catch {
                    // Malformed JSON — skip this variable record
                }
            }
            continue
        }
        const inputEntry = INPUT_SYS_ID_TO_FLUENT[variableSysId]
        if (inputEntry) {
            const shape = resolveVarEntryToShape(
                inputEntry,
                allowedInputFields,
                inputElementMappingByField,
                varRecord,
                activityPillContext
            )
            if (shape !== undefined) {
                inputs[inputEntry.name] = shape
            }
            continue
        }

        const experiencePropertyEntry = EXPERIENCE_PROPERTY_SYS_ID_TO_FLUENT[variableSysId]
        if (experiencePropertyEntry) {
            const shape = resolveVarEntryToShape(
                experiencePropertyEntry,
                allowedExperienceFields,
                expPropElementMappingByField,
                varRecord,
                activityPillContext
            )
            if (shape !== undefined) {
                experienceProperties[experiencePropertyEntry.name] = shape
            }
        }
    }

    // Build args array for 4-param API: Activity(def, config, inputs?, experienceProperties?)
    const args: Shape[] = [activityDefShape, configShape]

    const hasInputs = Object.keys(inputs).length > 0
    const hasExperienceProps = Object.keys(experienceProperties).length > 0

    if (hasInputs || hasExperienceProps) {
        // Add inputs (empty object if only experienceProperties exist)
        args.push(new ObjectShape({ source: record, properties: hasInputs ? inputs : {} }))
    }
    if (hasExperienceProps) {
        args.push(new ObjectShape({ source: record, properties: experienceProperties }))
    }

    const result: ActivityToShapeResult = {
        success: true,
        value: new CallExpressionShape({
            source: record,
            callee: ACTIVITY_GENERATED_CALLEE,
            args,
        }),
    }
    if (branchMap) {
        result.branchMap = branchMap
    }
    return result
}
